import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FiPlay, 
  FiPause, 
  FiVolume2, 
  FiVolumeX, 
  FiMaximize, 
  FiDownload, 
  FiSend,
  FiCheck,
  FiClock,
  FiFileText,
  FiVideo,
  FiBookOpen
} from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';

const CourseLearning = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [course, setCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [lessonMedia, setLessonMedia] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const videoContainerRef = useRef(null);

  const isDummyLesson = (lesson) => String(lesson?._id || '').startsWith('dummy');

  useEffect(() => {
    const container = videoContainerRef.current;
    if (!container) return undefined;

    const handleContextMenu = (event) => {
      event.preventDefault();
    };

    container.addEventListener('contextmenu', handleContextMenu);
    return () => container.removeEventListener('contextmenu', handleContextMenu);
  }, [currentLesson?._id, lessonMedia?.videoEmbedUrl]);



  const fetchCourseData = useCallback(async () => {
    try {
      const endpoint = `/api/learning/courses/${courseId}`;
      console.log('Fetching course data from:', endpoint);
      console.log('Course ID:', courseId);

      
      // Use absolute URL for production
      const baseURL = window.location.hostname === 'localhost' ? 'http://localhost:3001' : window.location.origin;
      const fullURL = `${baseURL}${endpoint}`;
      console.log('Full URL:', fullURL);
      
      const response = await axios.get(endpoint);
      console.log('Course data response:', response.data);
      const courseData = response.data;
      console.log('Processed course data:', courseData);
      console.log('Course units:', courseData.units);
      console.log('Units length:', courseData.units?.length);
      
      // Ensure course data has required fields
      if (!courseData._id || !courseData.title) {
        console.error('Invalid course data structure:', courseData);
        toast.error('강의 데이터 형식이 올바르지 않습니다.');
        setLoading(false);
        return;
      }
      
      setCourse(courseData);
      
      // Check for resume parameters in URL
      const unitIndexParam = searchParams.get('unitIndex');
      const lessonIndexParam = searchParams.get('lessonIndex');
      
      // Set lesson based on resume params or default to first lesson
      if (courseData.units && courseData.units.length > 0) {
        let targetLesson = null;
        
        // Try to resume from saved position
        if (unitIndexParam !== null && lessonIndexParam !== null) {
          const unitIndex = parseInt(unitIndexParam);
          const lessonIndex = parseInt(lessonIndexParam);
          
          if (courseData.units[unitIndex] && courseData.units[unitIndex].lessons[lessonIndex]) {
            targetLesson = courseData.units[unitIndex].lessons[lessonIndex];
            console.log('Resuming from saved lesson:', targetLesson.title);
          }
        }
        
        // Fallback to first lesson if no valid resume point
        if (!targetLesson && courseData.units[0].lessons.length > 0) {
          targetLesson = courseData.units[0].lessons[0];
        }
        
        if (targetLesson) {
          setCurrentLesson(targetLesson);
          setLoading(false);
        }
      } else {
        // Handle case where course has no units/lessons - show dummy content for testing
        console.log('Course has no units or lessons - showing dummy content');
        const dummyCourseData = {
          ...courseData,
          units: [
            {
              _id: 'dummy-unit-1',
              title: '기초 개념',
              description: '기본 개념과 원리 이해하기',
              order: 1,
              lessons: [
                {
                  _id: 'dummy-lesson-1',
                  title: '소개 및 개요',
                  description: '이 강의에서 배울 내용을 소개합니다',
                  content: '이 강의에서는 기본적인 개념과 원리를 배우게 됩니다. 차근차근 따라오시면 됩니다.',
                  videoUrl: '',
                  hasVideo: false,
                  duration: 10,
                  order: 1,

                  resources: [
                    {
                      title: '강의 자료 PDF',
                      fileUrl: 'https://drive.google.com/file/d/1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms/view?usp=sharing',
                      fileType: 'pdf',
                      description: '이번 강의의 핵심 내용을 정리한 자료입니다.'
                    }
                  ]
                },
                {
                  _id: 'dummy-lesson-2',
                  title: '환경 설정',
                  description: '개발 환경을 설정하는 방법',
                  content: '개발을 시작하기 전에 필요한 도구들을 설치하고 환경을 설정하는 방법을 배웁니다.',
                  videoUrl: '',
                  hasVideo: false,
                  duration: 15,
                  order: 2,

                  resources: [
                    {
                      title: '설치 가이드',
                      fileUrl: 'https://drive.google.com/file/d/1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms/view?usp=sharing',
                      fileType: 'pdf',
                      description: '단계별 설치 가이드'
                    }
                  ]
                },
                {
                  _id: 'dummy-lesson-3',
                  title: '첫 번째 프로젝트',
                  description: '실습을 통한 기본 개념 이해',
                  content: '이제 실제로 프로젝트를 만들어보면서 배운 내용을 적용해봅니다.',
                  videoUrl: '',
                  hasVideo: false,
                  duration: 20,
                  order: 3,
                  resources: []
                }
              ]
            },
            {
              _id: 'dummy-unit-2',
              title: '실전 적용',
              description: '배운 내용을 실제 프로젝트에 적용하기',
              order: 2,
              lessons: [
                {
                  _id: 'dummy-lesson-4',
                  title: '고급 기능 소개',
                  description: '더 심화된 기능들을 알아봅니다',
                  content: '기본 개념을 바탕으로 더 고급 기능들을 배워봅니다.',
                  videoUrl: '',
                  hasVideo: false,
                  duration: 25,
                  order: 1,
                  resources: [
                    {
                      title: '고급 기능 가이드',
                      fileUrl: 'https://example.com/advanced-guide.pdf',
                      fileType: 'pdf',
                      description: '고급 기능 사용법 가이드'
                    }
                  ]
                },
                {
                  _id: 'dummy-lesson-5',
                  title: '실전 프로젝트',
                  description: '완전한 프로젝트 만들기',
                  content: '지금까지 배운 모든 내용을 종합하여 완전한 프로젝트를 만들어봅니다.',
                  videoUrl: '',
                  hasVideo: false,
                  duration: 30,
                  order: 2,
                  resources: [
                    {
                      title: '프로젝트 템플릿',
                      fileUrl: 'https://example.com/project-template.zip',
                      fileType: 'zip',
                      description: '프로젝트 시작을 위한 템플릿'
                    }
                  ]
                }
              ]
            }
          ]
        };
        setCourse(dummyCourseData);
        setCurrentLesson(dummyCourseData.units[0].lessons[0]);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching course data:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      // Show error to user
      toast.error('강의 데이터를 불러오는데 실패했습니다.');
      setCourse(null);
      setComments([]);
      setCurrentLesson(null);
      setLoading(false);
    }
  }, [courseId, searchParams]);

  useEffect(() => {
    fetchCourseData();
  }, [fetchCourseData]);

  const lessonId = currentLesson?._id;
  const isDummy = isDummyLesson(currentLesson);
  const shouldLoadLessonMedia = Boolean(
    currentLesson?.hasVideo || (currentLesson?.resources?.length > 0)
  );

  useEffect(() => {
    if (!lessonId || !courseId) {
      setLessonMedia(null);
      return undefined;
    }

    if (isDummy) {
      setLessonMedia(null);
      return undefined;
    }

    if (!shouldLoadLessonMedia) {
      setLessonMedia(null);
      return undefined;
    }

    let cancelled = false;

    const fetchLessonMedia = async () => {
      setMediaLoading(true);
      setLessonMedia(null);

      try {
        const response = await axios.get(
          `/api/learning/courses/${courseId}/lessons/${lessonId}/media`
        );
        if (!cancelled) {
          setLessonMedia(response.data);
        }
      } catch (error) {
        console.error('Error fetching lesson media:', error);
        if (!cancelled) {
          toast.error('강의 미디어를 불러오지 못했습니다.');
          setLessonMedia(null);
        }
      } finally {
        if (!cancelled) {
          setMediaLoading(false);
        }
      }
    };

    fetchLessonMedia();

    return () => {
      cancelled = true;
    };
  }, [courseId, lessonId, isDummy, shouldLoadLessonMedia]);

  const fetchComments = useCallback(async () => {
    if (!currentLesson) return;
    
    try {
      const response = await axios.get(`/api/learning/lessons/${currentLesson._id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
      // Use mock comments when API fails
      const mockComments = [
        {
          _id: 'comment-1',
          user: {
            name: '김영수',
            avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
          },
          content: '강의가 정말 이해하기 쉽게 설명되어서 좋습니다!',
          createdAt: '2024-01-20T10:30:00Z',
          isInstructor: false
        },
        {
          _id: 'comment-2',
          user: {
            name: 'John Doe',
            avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
          },
          content: '좋은 질문이네요! HTML의 semantic 요소들은 웹 접근성과 SEO에 매우 중요합니다. 더 자세한 설명이 필요하시면 언제든 말씀해주세요.',
          createdAt: '2024-01-20T11:15:00Z',
          isInstructor: true
        },
        {
          _id: 'comment-3',
          user: {
            name: '이미영',
            avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face'
          },
          content: 'CSS에서 flexbox와 grid의 차이점이 궁금합니다.',
          createdAt: '2024-01-20T14:20:00Z',
          isInstructor: false
        }
      ];
      setComments(mockComments);
    }
  }, [currentLesson]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleLessonClick = async (unit, lesson) => {
    setCurrentLesson(lesson);
    setVideoProgress(0);
    setIsPlaying(false);
    
    // Save last viewed lesson for resume functionality
    try {
      // Find unit and lesson indices
      const unitIndex = course.units.findIndex(u => u._id === unit._id);
      const lessonIndex = unit.lessons.findIndex(l => l._id === lesson._id);
      
      if (unitIndex !== -1 && lessonIndex !== -1) {
        await axios.post(`/api/users/course-progress/${courseId}/last-viewed`, {
          unitIndex,
          lessonIndex
        });
      }
    } catch (error) {
      console.error('Error saving last viewed lesson:', error);
      // Don't show error to user, this is a background operation
    }
  };

  const handleVideoProgress = (e) => {
    const video = e.target;
    const progress = (video.currentTime / video.duration) * 100;
    setVideoProgress(progress);
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    // Mark lesson as completed
    if (currentLesson) {
      markLessonAsCompleted(currentLesson);
    }
  };

  const markLessonAsCompleted = async (lesson) => {
    try {
      // Update local state immediately for better UX
      setCurrentLesson({ ...lesson, completed: true });
      
      // Update the lesson in the course data
      setCourse(prevCourse => ({
        ...prevCourse,
        units: prevCourse.units.map(unit => ({
          ...unit,
          lessons: unit.lessons.map(l => 
            l._id === lesson._id ? { ...l, completed: true } : l
          )
        }))
      }));

      // Send completion to backend
      await axios.post(`/api/users/course-progress/${courseId}`, {
        lessonId: lesson._id,
        completed: true
      });

      // Find the next lesson and save it as resume point
      let nextUnitIndex = -1;
      let nextLessonIndex = -1;
      
      for (let unitIdx = 0; unitIdx < course.units.length; unitIdx++) {
        const unit = course.units[unitIdx];
        const lessonIdx = unit.lessons.findIndex(l => l._id === lesson._id);
        
        if (lessonIdx !== -1) {
          // Found current lesson, now find next lesson
          if (lessonIdx < unit.lessons.length - 1) {
            // Next lesson is in same unit
            nextUnitIndex = unitIdx;
            nextLessonIndex = lessonIdx + 1;
          } else if (unitIdx < course.units.length - 1) {
            // Next lesson is first lesson of next unit
            nextUnitIndex = unitIdx + 1;
            nextLessonIndex = 0;
          }
          break;
        }
      }
      
      // Save next lesson as resume point (if exists)
      if (nextUnitIndex !== -1 && nextLessonIndex !== -1) {
        try {
          await axios.post(`/api/users/course-progress/${courseId}/last-viewed`, {
            unitIndex: nextUnitIndex,
            lessonIndex: nextLessonIndex
          });
        } catch (error) {
          console.error('Error saving resume point:', error);
          // Don't show error for this background operation
        }
      }

      toast.success('강의를 완료했습니다!');
    } catch (error) {
      console.error('Error marking lesson as completed:', error);
      toast.error('진행 상황 저장에 실패했습니다');
    }
  };

  const markLessonAsIncomplete = async (lesson) => {
    try {
      // Update local state immediately for better UX
      setCurrentLesson({ ...lesson, completed: false });
      
      // Update the lesson in the course data
      setCourse(prevCourse => ({
        ...prevCourse,
        units: prevCourse.units.map(unit => ({
          ...unit,
          lessons: unit.lessons.map(l => 
            l._id === lesson._id ? { ...l, completed: false } : l
          )
        }))
      }));

      // Send completion to backend
      await axios.post(`/api/users/course-progress/${courseId}`, {
        lessonId: lesson._id,
        completed: false
      });

      toast.success('완료 표시를 취소했습니다');
    } catch (error) {
      console.error('Error marking lesson as incomplete:', error);
      toast.error('진행 상황 저장에 실패했습니다');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await axios.post(`/api/learning/lessons/${currentLesson._id}/comments`, {
        content: newComment
      });

      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('댓글이 등록되었습니다');
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error('댓글 등록에 실패했습니다');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}:${mins.toString().padStart(2, '0')}` : `${mins}분`;
  };

  const isIframeVideo = lessonMedia?.provider === 'youtube' || lessonMedia?.provider === 'google_drive';
  const displayResources = lessonMedia?.resources?.length
    ? lessonMedia.resources
    : (currentLesson?.resources || []);

    const getResourceIcon = (type) => {
    switch (type) {
      case 'pdf': return <FiFileText className="w-4 h-4" />;
      case 'video': return <FiVideo className="w-4 h-4" />;
      case 'zip': return <FiDownload className="w-4 h-4" />;
      default: return <FiFileText className="w-4 h-4" />;
    }
  };

  // Calculate course progress
  const calculateProgress = () => {
    if (!course || !course.units) return 0;
    
    const totalLessons = course.units.reduce((total, unit) => total + unit.lessons.length, 0);
    const completedLessons = course.units.reduce((total, unit) => 
      total + unit.lessons.filter(lesson => lesson.completed).length, 0
    );
    
    return totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  };

  const progress = calculateProgress();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto"></div>
          <p className="mt-4 text-gray-600">강의를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">강의를 찾을 수 없습니다</h2>
          <p className="text-gray-600">강의가 존재하지 않거나 접근 권한이 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/student-dashboard')}
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                ← 
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {course.title}

                  {course.units && course.units.some(unit => unit._id.startsWith('dummy')) && (
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">테스트 콘텐츠</span>
                  )}
                </h1>
                <p className="text-sm text-gray-600">강사: {course.createdBy?.name || course.instructor?.name || 'Unknown'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">진행률: {progress}%</span>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area - Fixed Height */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Course Units and Lessons */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">강의 목록</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {course.units && course.units.length > 0 ? (
                course.units.map((unit) => (
                  <div key={unit._id} className="border border-gray-200 rounded-lg">
                    <div className="p-3 bg-gray-50 border-b border-gray-200">
                      <h3 className="font-medium text-gray-900">{unit.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{unit.description}</p>
                    </div>
                    <div className="p-2">
                      {unit.lessons.map((lesson) => (
                        <button
                          key={lesson._id}
                          onClick={() => handleLessonClick(unit, lesson)}
                          className={`w-full text-left p-3 rounded-lg transition-colors ${
                            currentLesson?._id === lesson._id
                              ? 'bg-primary-50 text-primary-900 border border-primary-200'
                              : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              {lesson.completed ? (
                                <FiCheck className="w-4 h-4 text-green-600" />
                              ) : (
                                <FiPlay className="w-4 h-4 text-gray-400" />
                              )}
                              <span className="text-sm font-medium">{lesson.title}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <FiClock className="w-3 h-3" />
                              <span>{formatDuration(lesson.duration)}</span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <FiBookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">강의 내용이 없습니다</h3>
                  <p className="text-gray-500">이 강의에는 아직 단원이나 강의가 설정되지 않았습니다.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">
          {currentLesson ? (
            <>
              {/* Video Player - Fixed Height */}
              <div className=" flex-shrink-0">
                <div className="flex justify-center items-center py-8">
                  <div ref={videoContainerRef} className="w-1/2 relative aspect-video bg-gray-200 rounded-lg overflow-hidden select-none">
                    {mediaLoading ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900/5">
                        <div className="loading-spinner"></div>
                      </div>
                    ) : lessonMedia?.videoEmbedUrl && isIframeVideo ? (
                      <iframe
                        key={lessonMedia.videoEmbedUrl}
                        src={lessonMedia.videoEmbedUrl}
                        title={currentLesson.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          pointerEvents: 'auto',
                          userSelect: 'none'
                        }}
                        sandbox="allow-scripts allow-same-origin allow-presentation"
                      />
                    ) : lessonMedia?.videoEmbedUrl ? (
                      <video
                        className="w-full h-full object-contain"
                        onTimeUpdate={handleVideoProgress}
                        onEnded={handleVideoEnded}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        controlsList="nodownload nofullscreen noremoteplayback"
                        disablePictureInPicture
                        disableRemotePlayback
                        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                      >
                        <source src={lessonMedia.videoEmbedUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm px-6 text-center">
                        {currentLesson.hasVideo
                          ? '영상을 불러올 수 없습니다. 잠시 후 다시 시도해주세요.'
                          : '등록된 영상이 없습니다.'}
                      </div>
                    )}
                    
                    {lessonMedia?.videoEmbedUrl && !isIframeVideo && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => {
                              const video = document.querySelector('video');
                              if (isPlaying) {
                                video.pause();
                              } else {
                                video.play();
                              }
                            }}
                            className="text-white hover:text-gray-300 transition-colors"
                          >
                            {isPlaying ? <FiPause className="w-6 h-6" /> : <FiPlay className="w-6 h-6" />}
                          </button>
                          
                          <div className="flex-1 bg-gray-600 rounded-full h-2">
                            <div 
                              className="bg-white h-2 rounded-full" 
                              style={{ width: `${videoProgress}%` }}
                            ></div>
                          </div>
                          
                          <button
                            onClick={() => {
                              const video = document.querySelector('video');
                              video.muted = !isMuted;
                              setIsMuted(!isMuted);
                            }}
                            className="text-white hover:text-gray-300 transition-colors"
                          >
                            {isMuted ? <FiVolumeX className="w-5 h-5" /> : <FiVolume2 className="w-5 h-5" />}
                          </button>
                          
                          <button 
                            className="text-white hover:text-gray-300 transition-colors"
                            onClick={() => {
                              const container = videoContainerRef.current;
                              if (!container) return;
                              if (document.fullscreenElement) {
                                document.exitFullscreen().catch(() => {});
                              } else {
                                if (container.requestFullscreen) {
                                  container.requestFullscreen().catch(() => {});
                                }
                              }
                            }}
                          >
                            <FiMaximize className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Lesson Content - Scrollable */}
              <div className="flex-1 overflow-y-auto">
                <div className="max-w-4xl mx-auto p-12">
                  {/* Lesson Info */}
                  <div className="mb-12">
                    <div className="flex items-center justify-between mb-4">
                      <h1 className="text-2xl font-bold text-gray-900">{currentLesson.title}</h1>
                      <button
                        onClick={() => currentLesson.completed 
                          ? markLessonAsIncomplete(currentLesson)
                          : markLessonAsCompleted(currentLesson)
                        }
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                          currentLesson.completed
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        <FiCheck className="w-4 h-4" />
                        <span>{currentLesson.completed ? '완료됨' : '완료 표시'}</span>
                      </button>
                    </div>
                    <p className="text-gray-600 mb-6">{currentLesson.description}</p>
                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <span className="flex items-center space-x-2">
                        <FiClock className="w-4 h-4" />
                        <span>{formatDuration(currentLesson.duration)}</span>
                      </span>
                      <span className="flex items-center space-x-2">
                        <FiBookOpen className="w-4 h-4" />
                        <span>{currentLesson.type === 'video' ? '비디오 강의' : '텍스트 강의'}</span>
                      </span>
                    </div>
                  </div>

                  {/* Resources Section */}
                  {displayResources.length > 0 && (
                    <div className="mb-12">
                      <h3 className="text-lg font-semibold text-gray-900 mb-6">강의 자료</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {displayResources.map((resource, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-4 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div className="text-primary-600">
                              {getResourceIcon(resource.fileType || resource.type)}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{resource.title || resource.name}</h4>
                              <p className="text-sm text-gray-500">{resource.description || resource.size}</p>
                            </div>
                            <button 
                              onClick={() => {
                                if (resource.accessUrl) {
                                  window.open(resource.accessUrl, '_blank');
                                } else {
                                  toast.error('자료 링크를 불러오지 못했습니다.');
                                }
                              }}
                              disabled={!resource.accessUrl}
                              className="text-primary-600 hover:text-primary-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              title="다운로드"
                            >
                              <FiDownload className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Comments Section */}
                  <div className="border-t border-gray-200 pt-12">
                    <div className="flex items-center justify-between mb-8">
                      <h3 className="text-lg font-semibold text-gray-900">질문 및 댓글</h3>
                      <button
                        onClick={() => setShowComments(!showComments)}
                        className="text-primary-600 hover:text-primary-700 transition-colors"
                      >
                        {showComments ? '숨기기' : '보기'}
                      </button>
                    </div>

                    {showComments && (
                      <>
                        {/* Comment Form */}
                        <div className="mb-8">
                          <form onSubmit={handleSubmitComment} className="space-y-6">
                              <div>
                                <textarea
                                  value={newComment}
                                  onChange={(e) => setNewComment(e.target.value)}
                                  placeholder="질문이나 댓글을 남겨주세요..."
                                  rows={4}
                                  className="w-full px-6 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                                />
                              </div>
                              <div className="flex justify-end">
                                <button
                                  type="submit"
                                  disabled={submittingComment || !newComment.trim()}
                                  className="flex items-center space-x-3 px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {submittingComment ? (
                                    <>
                                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                      <span>등록 중...</span>
                                    </>
                                  ) : (
                                    <>
                                      <FiSend className="w-5 h-5" />
                                      <span>댓글 등록</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </form>
                        </div>

                        {/* Comments List */}
                        <div className="space-y-8">
                          {comments.map((comment) => (
                            <div key={comment._id} className="flex space-x-4">
                              <div className="flex-shrink-0">
                                <img
                                  src={comment.user.avatar}
                                  alt={comment.user.name}
                                  className="w-12 h-12 rounded-full"
                                />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-3">
                                  <h4 className="font-medium text-gray-900">{comment.user.name}</h4>
                                  {comment.isInstructor && (
                                    <span className="px-3 py-1 text-xs bg-primary-100 text-primary-800 rounded-full">
                                      강사
                                    </span>
                                  )}
                                  <span className="text-sm text-gray-500">
                                    {new Date(comment.createdAt).toLocaleDateString('ko-KR')}
                                  </span>
                                </div>
                                <p className="text-gray-700 leading-relaxed">{comment.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FiPlay className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">강의를 선택하세요</h3>
                <p className="text-gray-600">왼쪽에서 시작할 강의를 선택해주세요.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Minimal Copyright Footer */}
      <div className="bg-white border-t border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <p className="text-xs text-gray-400 text-center">
            © 2025 EnglishPT. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CourseLearning; 